import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Target, Coins, Radio, Users, Vote } from "lucide-react";

/**
 * StreamerToolsWidgets — public-facing Goals + Poll for `/c/:username`.
 *
 * Renders nothing when the streamer has no active goals or polls, so we
 * don't take up space needlessly. Polling every 4s keeps tallies fresh
 * without hammering the API.
 */
const ICON = { donation: Coins, subscribers: Users, followers: Radio };
const COLOR = { donation: "text-orange-400 bg-orange-500", subscribers: "text-cyan-400 bg-cyan-500", followers: "text-pink-400 bg-pink-500" };

export function StreamGoalsWidget({ channel }) {
  const [goals, setGoals] = useState([]);
  const load = useCallback(() => {
    if (!channel) return;
    api.get(`/channels/${channel}/goals`).then((r) => setGoals(r.data.goals || [])).catch(() => {});
  }, [channel]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  if (!goals.length) return null;
  return (
    <div className="rounded-xl p-4" style={{ background: "#0e0e13", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="stream-goals-widget">
      <div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-cyan-400" /><span className="sc-mono text-[11px] tracking-widest text-zinc-400">STREAM GOALS</span></div>
      <div className="space-y-3">
        {goals.map((g) => {
          const I = ICON[g.type] || Coins;
          const [textColor, bg] = (COLOR[g.type] || COLOR.donation).split(" ");
          const pct = Math.min(100, Math.round((g.current / Math.max(g.target, 1)) * 100));
          return (
            <div key={g.id} data-testid={`goal-widget-${g.id}`}>
              <div className="flex items-center gap-2 mb-1"><I className={`w-3.5 h-3.5 ${textColor}`} /><span className="text-sm font-medium">{g.label}</span></div>
              <div className="h-2 bg-zinc-800 rounded overflow-hidden"><div className={`h-full ${bg}`} style={{ width: `${pct}%` }} /></div>
              <div className="mt-1 text-[11px] sc-mono text-zinc-500">{g.current.toFixed(1)} / {g.target} · {pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LivePollWidget({ channel }) {
  const { user } = useAuth();
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(new Set());
  const load = useCallback(() => {
    if (!channel) return;
    api.get(`/channels/${channel}/polls/active`).then((r) => setPoll(r.data.poll || null)).catch(() => {});
  }, [channel]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);

  if (!poll || poll.closed) return null;

  const vote = async (i) => {
    if (!user) return toast.error("Log in to vote");
    const next = new Set(voted);
    if (poll.allow_multi) {
      next.has(i) ? next.delete(i) : next.add(i);
    } else {
      next.clear();
      next.add(i);
    }
    setVoted(next);
    try {
      const r = await api.post(`/channels/${channel}/polls/${poll.id}/vote`, { choices: Array.from(next) });
      setPoll(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Vote failed"); }
  };

  return (
    <div className="rounded-xl p-4 border border-pink-500/30" style={{ background: "linear-gradient(180deg, rgba(255,16,83,0.06), #0e0e13)" }} data-testid="live-poll-widget">
      <div className="flex items-center gap-2 mb-2"><Vote className="w-4 h-4 text-pink-400" /><span className="sc-mono text-[11px] tracking-widest text-pink-400">LIVE POLL</span></div>
      <div className="text-sm font-semibold mb-2">{poll.question}</div>
      <div className="space-y-2">
        {poll.choices.map((c, i) => {
          const tally = poll.tallies[i] || 0;
          const pct = poll.total_votes ? Math.round((tally / poll.total_votes) * 100) : 0;
          const mine = voted.has(i);
          return (
            <button key={i} onClick={() => vote(i)} className={`w-full text-left rounded border ${mine ? "border-cyan-400 bg-cyan-500/10" : "border-white/10 hover:border-white/25 bg-zinc-900"} p-2 transition`} data-testid={`poll-choice-btn-${i}`}>
              <div className="flex justify-between text-xs"><span>{c}</span><span className="sc-mono text-zinc-500">{tally} · {pct}%</span></div>
              <div className="mt-1 h-1.5 bg-zinc-800 rounded overflow-hidden"><div className="h-full bg-pink-400" style={{ width: `${pct}%` }} /></div>
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] sc-mono text-zinc-500">{poll.total_votes} votes · ends {new Date(poll.ends_at).toLocaleTimeString()}</div>
    </div>
  );
}
